import React, { useId } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FIELD_TAGS = ["input", "select", "textarea"];

// Clones an id + validation aria-attrs onto the actual form control inside
// `node`, looking one level into a wrapper element (e.g. a `<div>` used to
// position an icon/toggle button next to the input) if `node` itself isn't
// a field. Leaves any id the field already sets explicitly untouched.
function injectFieldProps(node, id, extraProps) {
  if (!React.isValidElement(node)) return node;

  if (typeof node.type === "string" && FIELD_TAGS.includes(node.type)) {
    return React.cloneElement(node, {
      id: node.props.id || id,
      ...extraProps,
    });
  }

  if (node.props?.children) {
    let injected = false;
    const newChildren = React.Children.map(node.props.children, (child) => {
      if (
        !injected &&
        React.isValidElement(child) &&
        typeof child.type === "string" &&
        FIELD_TAGS.includes(child.type)
      ) {
        injected = true;
        return React.cloneElement(child, {
          id: child.props.id || id,
          ...extraProps,
        });
      }
      return child;
    });
    if (injected) {
      return React.cloneElement(node, {}, newChildren);
    }
  }

  return node;
}

const FormRow = ({
  children,
  label,
  lable,
  error,
  extend = false,
  isValid = null,
  hint = null,
}) => {
  const fieldLabel = label ?? lable;
  const generatedId = useId();
  const errorId = `${generatedId}-error`;
  const hintId = `${generatedId}-hint`;

  const fieldChild = injectFieldProps(children, generatedId, {
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? errorId : hint ? hintId : undefined,
  });

  return (
    <div className={`${extend ? " col-span-2" : ""} space-y-2`}>
      {fieldLabel && (
        <label
          htmlFor={generatedId}
          className="block text-sm font-medium capitalize text-gray-700 dark:text-gray-300"
        >
          {fieldLabel}
        </label>
      )}
      <div className=" space-y-1">
        <div className="relative">
          {fieldChild}
          {isValid === true && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <svg
                className="h-5 w-5 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </motion.div>
          )}
        </div>
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              key="error"
              id={errorId}
              role="alert"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className=" text-xs font-medium text-red-400 dark:text-red-300"
            >
              {error}
            </motion.p>
          )}
          {hint && !error && (
            <motion.p
              key="hint"
              id={hintId}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className=" text-xs font-medium text-gray-400 dark:text-gray-500"
            >
              {hint}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FormRow;
